import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import PushPinIcon from '@mui/icons-material/PushPin';
import { Link as RouterLink } from 'react-router-dom';
import {
  deleteAnnouncement,
  listAnnouncements,
  publishAnnouncement,
  type PublicAnnouncement,
} from '../../lib/announcements-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';
import { CategoryChip } from './category-chip';

export function AnnouncementsListPage() {
  const t = useT();
  const [items, setItems] = useState<PublicAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const { items } = await listAnnouncements({ limit: 50 });
      setItems(items);
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function handlePublish(id: string) {
    setBusyId(id);
    try {
      const out = await publishAnnouncement(id);
      await refresh();
      alert(t('ann.publishedMsg', { count: out.notification.total_recipients }));
    } catch (e) {
      alert(extractApiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(id: string, title: string) {
    if (!confirm(t('ann.deleteConfirm', { title }))) return;
    setBusyId(id);
    try {
      await deleteAnnouncement(id);
      await refresh();
    } catch (e) {
      alert(extractApiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <div>
          <Typography variant="h4" fontWeight={600}>
            {t('ann.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('ann.subtitle')}
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/announcements/new"
        >
          {t('ann.newBtn')}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('ann.empty')}</Typography>
              <Button
                variant="text"
                component={RouterLink}
                to="/announcements/new"
                sx={{ mt: 1 }}
              >
                {t('ann.composeFirst')}
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }}></TableCell>
                    <TableCell>{t('ann.colTitle')}</TableCell>
                    <TableCell sx={{ width: 130 }}>{t('ann.colCategory')}</TableCell>
                    <TableCell sx={{ width: 110 }}>{t('ann.colStatus')}</TableCell>
                    <TableCell sx={{ width: 170 }}>{t('ann.colPublished')}</TableCell>
                    <TableCell align="right" sx={{ width: 160 }}>
                      {t('common.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((a) => {
                    const isPublished = a.published_at !== null;
                    const isBusy = busyId === a.id;
                    return (
                      <TableRow key={a.id} hover>
                        <TableCell>
                          {a.pinned ? (
                            <PushPinIcon fontSize="small" color="primary" />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {a.title}
                          </Typography>
                          {a.require_ack ? (
                            <Chip
                              label={t('ann.requiresAck')}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <CategoryChip category={a.category} />
                        </TableCell>
                        <TableCell>
                          {isPublished ? (
                            <Chip label={t('ann.statusPublished')} size="small" color="success" />
                          ) : (
                            <Chip label={t('ann.statusDraft')} size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {a.published_at
                              ? new Date(a.published_at).toLocaleString('id-ID', {
                                  dateStyle: 'medium',
                                  timeStyle: 'short',
                                })
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {!isPublished ? (
                              <IconButton
                                size="small"
                                title={t('ann.publish')}
                                disabled={isBusy}
                                onClick={() => handlePublish(a.id)}
                              >
                                <PublishIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                            <IconButton
                              size="small"
                              title={t('common.edit')}
                              disabled={isBusy}
                              component={RouterLink}
                              to={`/announcements/${a.id}/edit`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title={t('common.delete')}
                              disabled={isBusy}
                              onClick={() => handleDelete(a.id, a.title)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
